/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import React, { useMemo, useState } from 'react';
import { Button, Input, List, Space, Typography } from 'antd';
import { useIntl } from 'react-intl';
import { ThemedStyleFn, useStyles } from '~src/hooks/useStyles';
import { useQuery } from 'react-query';
import {
  getQuestionsData,
  getQuestionsDataVendors,
} from '~src/services/api/questions';
import { formatDate } from '~src/utils/formatDate';
import IconButton from '~components/_common/IconButton/IconButton';

type CorrectionLetter = {
  id: number;
  title: string;
  description: string;
  status: number;
  movement_ids?: number[];
  comment_ids?: number[];
  question_ids?: number[];
  modification_report?: { file_guid: string; name: string } | null;
  attachments?: { file_guid: string; name: string }[];
};

export type QuestionsTabProps = {
  loading: boolean;
  error: boolean;
  isBusy: boolean;

  projectId: number;
  tenderId: number;

  letter?: CorrectionLetter;

  onManageQuestions: (
    action: 'add_question' | 'remove_question' | 'replace_question',
    questionIds: number[]
  ) => void;
  readOnly: boolean;
  shareId?: string;
  shareKey?: string;
};

export const QuestionsTab: React.FC<QuestionsTabProps> = ({
  loading,
  error,
  isBusy,
  projectId,
  tenderId,
  letter,
  onManageQuestions,
  readOnly,
  shareId,
  shareKey,
}) => {
  const intl = useIntl();
  const styles = useStyles(styleFn);
  const isVendorSide = !!shareId && !!shareKey;

  // ==state
  const [searchText, setSearchText] = useState('');

  // ==constants
  const questionIds = useMemo(() => {
    // backend example shows comment_ids, manage endpoint uses question_ids
    const ids = letter?.question_ids || letter?.comment_ids || [];
    return ids;
  }, [letter?.question_ids, letter?.comment_ids]);

  const questionIdsParam = useMemo(() => {
    if (!questionIds.length) return '';
    return questionIds.join(',');
  }, [questionIds]);

  const selectedQuestionsQuery = useQuery<any[]>(
    [
      'questions',
      isVendorSide ? shareId : projectId,
      tenderId,
      questionIdsParam,
    ],
    () =>
      isVendorSide
        ? getQuestionsDataVendors({
            shareId: shareId!,
            shareKey: shareKey!,
            question_ids: questionIdsParam,
          })
        : getQuestionsData({
            project_id: Number(projectId),
            tender_id: Number(tenderId),
            question_ids: questionIdsParam,
          }),
    {
      enabled:
        (isVendorSide ? !!shareId : !!projectId && !!tenderId) &&
        !!questionIdsParam,
      refetchOnWindowFocus: false,
    }
  );

  const selectedQuestionsById = useMemo(() => {
    const map = new Map<number, any>();
    (selectedQuestionsQuery.data || []).forEach((q) => {
      const id = Number(q?.id);
      if (!Number.isNaN(id)) map.set(id, q);
    });
    return map;
  }, [selectedQuestionsQuery.data]);

  // ==filtering
  const filteredQuestionIds = useMemo(() => {
    if (!searchText.trim()) return questionIds;

    const searchLower = searchText.toLowerCase();

    return questionIds.filter((id) => {
      const q = selectedQuestionsById.get(Number(id));
      if (!q) return false;

      // Search in question text
      const questionText = (q?.question || '').toLowerCase();
      if (questionText.includes(searchLower)) return true;

      // Search in author name
      const authorName =
        (q?.created_by?.full_name || '').toLowerCase() +
        ' ' +
        (q?.created_by?.company_name || '').toLowerCase();
      if (authorName.includes(searchLower)) return true;

      // Search in answers (ignoring removed/rejected)
      const answers = Array.isArray(q?.answers)
        ? q.answers
            .filter((a: any) => a.state !== 5 && a.state !== 9)
            .map(
              (a: any) =>
                `${a?.answer || ''} ${a?.created_by?.full_name || ''} ${
                  a?.created_by?.company_name || ''
                }`
            )
            .join(' ')
            .toLowerCase()
        : '';
      if (answers.includes(searchLower)) return true;

      // Search in ID
      if (String(id).includes(searchLower)) return true;

      return false;
    });
  }, [questionIds, selectedQuestionsById, searchText]);

  // ==return
  if (loading) {
    return (
      <div css={styles.stateBox}>
        <Typography.Text type="secondary">
          {intl.formatMessage({ id: 'loading' })}
        </Typography.Text>
      </div>
    );
  }

  if (error || !letter) {
    return (
      <div css={styles.stateBox}>
        <Typography.Text type="danger">
          {intl.formatMessage({ id: 'correction-letter-load-failed' })}
        </Typography.Text>
      </div>
    );
  }

  return (
    <div css={styles.wrapper}>
      <Typography.Text type="secondary" css={styles.helpText}>
        {intl.formatMessage({ id: 'correction-letter-qa-help' })}
      </Typography.Text>

      {!readOnly && (
        <div css={styles.instructionBox}>
          <Typography.Text type="secondary" css={styles.instructionText}>
            {intl.formatMessage({
              id: 'correction-letter-add-questions-instruction',
              defaultMessage:
                'Use the "Add to Correction Letter" option from the right-click context menu in the Q&A table to add questions.',
            })}
          </Typography.Text>
        </div>
      )}

      <Input
        prefix={<IconButton iconStyle="far" icon="search" backgroundOff />}
        size={'small'}
        placeholder={intl.formatMessage({ id: 'search' })}
        value={searchText}
        onChange={(e) => setSearchText(e.target.value)}
        css={styles.searchInput}
      />

      <div css={styles.listSlider}>
        <List
          size="small"
          bordered
          dataSource={filteredQuestionIds}
          locale={{
            emptyText: intl.formatMessage({
              id: 'correction-letter-empty-qa',
            }),
          }}
          renderItem={(id) => {
            const q = selectedQuestionsById.get(Number(id));

            const questionText: string = q?.question || '';
            const authorName: string =
              q?.created_by?.full_name || q?.created_by?.company_name || '';
            const createdAt: any = q?.created_at;

            const answers: any[] = Array.isArray(q?.answers)
              ? q.answers.filter((a: any) => a.state !== 5 && a.state !== 9)
              : [];

            return (
              <List.Item
                actions={[
                  !readOnly && (
                    <Button
                      key="rm"
                      size="small"
                      danger
                      disabled={isBusy}
                      onClick={() =>
                        onManageQuestions('remove_question', [id])
                      }>
                      {intl.formatMessage({ id: 'remove' })}
                    </Button>
                  ),
                ]}>
                <Space direction="vertical" size={4}>
                  {!!questionText ? (
                    <Typography.Text
                      style={{
                        whiteSpace: 'pre-line',
                        wordBreak: 'break-word',
                      }}>
                      {questionText}
                    </Typography.Text>
                  ) : (
                    <Space>
                      <Typography.Text strong>#</Typography.Text>
                      <Typography.Text>{id}</Typography.Text>
                    </Space>
                  )}

                  {(!!authorName || !!createdAt) && (
                    <Typography.Text type="secondary">
                      {authorName
                        ? `${authorName}${createdAt ? ' • ' : ''}`
                        : ''}
                      {createdAt ? formatDate(createdAt, 'long') : ''}
                    </Typography.Text>
                  )}

                  {!!answers.length && (
                    <div css={styles.answersBox}>
                      {answers.map((a) => {
                        const answerText: string = a?.answer || '';
                        const aAuthor: string =
                          a?.created_by?.full_name ||
                          a?.created_by?.company_name ||
                          '';
                        const aCreatedAt: any = a?.created_at;

                        return (
                          <div key={a?.id} css={styles.answerItem}>
                            {!!answerText && (
                              <Typography.Text
                                style={{
                                  whiteSpace: 'pre-line',
                                  wordBreak: 'break-word',
                                }}>
                                {answerText}
                              </Typography.Text>
                            )}

                            {(!!aAuthor || !!aCreatedAt) && (
                              <Typography.Text type="secondary">
                                {aAuthor
                                  ? `${aAuthor}${aCreatedAt ? ' • ' : ''}`
                                  : ''}
                                {aCreatedAt
                                  ? formatDate(aCreatedAt, 'long')
                                  : ''}
                              </Typography.Text>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </Space>
              </List.Item>
            );
          }}
        />
      </div>
    </div>
  );
};

const styleFn: ThemedStyleFn = () => ({
  wrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: 14,
    paddingBottom: 18,
    paddingTop: 5,
  },
  stateBox: {
    padding: 16,
    background: '#fafafa',
    borderRadius: 6,
    border: '1px solid #f0f0f0',
  },
  helpText: {
    fontSize: 12,
    lineHeight: 1.35,
  },
  instructionBox: {
    padding: 10,
    backgroundColor: '#f6f8fb',
    border: '1px solid #d9e8f2',
    borderRadius: 4,
    marginBottom: 8,
  },
  instructionText: {
    fontSize: 12,
    lineHeight: 1.4,
  },
  searchInput: {},
  listSlider: {
    maxHeight: 300,
    overflowY: 'auto',
    border: '1px solid #f0f0f0',
    borderRadius: 4,
  },
  answersBox: {
    marginTop: 4,
    paddingLeft: 12,
    borderLeft: '2px solid #e6e6e6',
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
  },
  answerItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
  },
});
